import {
  ACTIVE_APPEARANCE_KEY,
  type AppearanceMode,
  DARK_SCHEME_QUERY,
  DEFAULT_APPEARANCE,
  type ResolvedAppearance,
  appearanceStorageKey,
  isAppearanceMode,
  readAppearance,
  resolveAppearance,
  writeActiveAppearance,
  writeAppearance
} from "./appearance-preference"
import { flushSync } from "react-dom"

export type AppearanceState = {
  userId: string | null
  mode: AppearanceMode
  resolved: ResolvedAppearance
  /** False when the browser refused the last write. */
  persisted: boolean
}

const THEME_COLOR: Record<ResolvedAppearance, string> = {
  light: "#ffffff",
  dark: "#151a21"
}

const listeners = new Set<() => void>()
let systemQuery: MediaQueryList | null = null
// Printed and exported pages are paper, whatever the screen shows.
let printing = false

const systemPrefersDark = () => {
  try {
    return !!window.matchMedia?.(DARK_SCHEME_QUERY)?.matches
  } catch {
    return false
  }
}

// Starts from what the boot script painted, so the first render's antd theme
// matches the page until the account is bound.
const bootMode = (): AppearanceMode => {
  try {
    const stored = window.localStorage.getItem(ACTIVE_APPEARANCE_KEY)
    return isAppearanceMode(stored) ? stored : DEFAULT_APPEARANCE
  } catch {
    return DEFAULT_APPEARANCE
  }
}

const initialState = (): AppearanceState => {
  const mode = typeof window === "undefined" ? DEFAULT_APPEARANCE : bootMode()
  return {
    userId: null,
    mode,
    resolved:
      typeof window === "undefined"
        ? "light"
        : resolveAppearance(mode, systemPrefersDark()),
    persisted: true
  }
}

let state: AppearanceState = initialState()

const applyToDocument = (resolved: ResolvedAppearance) => {
  const root = document.documentElement
  if (root.dataset.theme !== resolved) root.dataset.theme = resolved
  root.style.colorScheme = resolved
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute("content", THEME_COLOR[resolved])
}

const commit = (next: Omit<AppearanceState, "resolved">) => {
  const resolved = printing
    ? "light"
    : resolveAppearance(next.mode, systemPrefersDark())
  const changed =
    next.userId !== state.userId ||
    next.mode !== state.mode ||
    resolved !== state.resolved ||
    next.persisted !== state.persisted
  state = { ...next, resolved }
  applyToDocument(resolved)
  if (changed) listeners.forEach((listener) => listener())
}

const onSystemChange = () => {
  if (state.mode === "system") commit(state)
}

// Another tab of the same account changed the choice.
const onStorage = (event: StorageEvent) => {
  if (!state.userId || event.key !== appearanceStorageKey(state.userId)) return
  const mode = isAppearanceMode(event.newValue)
    ? event.newValue
    : DEFAULT_APPEARANCE
  commit({ ...state, mode })
}

// The browser snapshots the page right after `beforeprint`, so antd's tokens
// and chart inks have to re-render light synchronously, not on the next frame.
const onBeforePrint = () => {
  printing = true
  flushSync(() => commit(state))
}

const onAfterPrint = () => {
  printing = false
  commit(state)
}

const listenOnce = () => {
  if (systemQuery || typeof window === "undefined") return
  try {
    systemQuery = window.matchMedia?.(DARK_SCHEME_QUERY) ?? null
    systemQuery?.addEventListener?.("change", onSystemChange)
  } catch {
    systemQuery = null
  }
  window.addEventListener("storage", onStorage)
  window.addEventListener("beforeprint", onBeforePrint)
  window.addEventListener("afterprint", onAfterPrint)
}

export const getAppearanceState = () => state

export const subscribeAppearance = (listener: () => void) => {
  listenOnce()
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * Follows the signed-in account. Signed out, the app is light and the boot
 * mirror is cleared, so the next account on this browser never starts in the
 * previous one's appearance.
 */
export const bindAppearanceUser = (userId: string | number | null) => {
  listenOnce()
  const id = userId == null ? null : String(userId)
  if (id === state.userId && id !== null) return
  const mode = id ? readAppearance(id) : DEFAULT_APPEARANCE
  writeActiveAppearance(id ? mode : null)
  commit({ userId: id, mode, persisted: true })
}

export const setAppearanceMode = (mode: AppearanceMode) => {
  if (!state.userId) return
  const persisted = writeAppearance(state.userId, mode)
  commit({ ...state, mode, persisted })
}

/** Test seam: drop listeners and return to the signed-out default. */
export const resetAppearanceStoreForTests = () => {
  systemQuery?.removeEventListener?.("change", onSystemChange)
  systemQuery = null
  window.removeEventListener("storage", onStorage)
  window.removeEventListener("beforeprint", onBeforePrint)
  window.removeEventListener("afterprint", onAfterPrint)
  printing = false
  listeners.clear()
  state = initialState()
  delete document.documentElement.dataset.theme
  document.documentElement.style.colorScheme = ""
}
